import { useEffect, useCallback, useMemo, memo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  isCreatedOption,
  MethodsOfUseForm,
  OptionType,
  CreatedOption,
} from "../../types/types";
import { DeletableChips } from "../DeletableChips/DeletableChips";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { AccessibilityData } from "../../types/types.PIM";
import { strings } from "../../util/strings";

interface ChipData {
  name: string;
  id: string;
}

interface MultiSelectFieldProps {
  id: string;
  fieldValues: string[];
  display_name: string;
  methodsOfUseForm: MethodsOfUseForm;
  multiSelectRefs: React.MutableRefObject<{
    [key: string]: { clearChips: () => void };
  }>;
  baseUrl: string;
  existingData?: AccessibilityData[];
  userAndRolesValues?: string[];
}

const CompactInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 515px;
  padding: 8px 0px 8px;
`;

const ALLOW_ALL_OPTIONS = {
  allow_all_roles: "All Roles",
  allow_all_customers: "All Customers",
  allow_all_distributors: "All Distributors",
} as const;

type AllowAllOptionKey = keyof typeof ALLOW_ALL_OPTIONS;

const isAllowAllOption = (value: string): value is AllowAllOptionKey => {
  return value in ALLOW_ALL_OPTIONS;
};

const getAllowAllIds = (): string[] => Object.keys(ALLOW_ALL_OPTIONS);

const formatUserName = (data: AccessibilityData): string => {
  const { firstname, lastname, name } = data;
  const fullName = `${firstname || ""} ${lastname || ""}`.trim();
  return fullName || name || "Unknown User";
};

const useChipManager = (
  id: string,
  fieldValues: string[],
  existingData: AccessibilityData[] | undefined,
  isUserEndpoint: boolean,
  setValue: (name: string, value: any) => void,
  setError: (name: string, error: { message: string }) => void,
  clearErrors: (name: string) => void,
  t: any,
  otherFieldValues?: string[],
  getValues?: () => any
) => {
  const [chips, setChips] = useState<ChipData[]>([]);
  const [validationError, setValidationError] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState(false);
  const previousChipIdsRef = useRef<string[]>([]);
  const isInitializedRef = useRef(false);
  const previousOtherFieldValuesRef = useRef<string[]>([]);

  const chipsChanged = useCallback(
    (oldIds: string[], newIds: string[]): boolean => {
      if (oldIds.length !== newIds.length) return true;
      return oldIds.some((id, index) => id !== newIds[index]);
    },
    []
  );

  const fieldValuesKey = useMemo(() => {
    return fieldValues ? fieldValues.sort().join(",") : "";
  }, [fieldValues]);

  const existingDataKey = useMemo(() => {
    return existingData
      ? existingData
          .map((d) => d.id)
          .sort()
          .join(",")
      : "";
  }, [existingData]);

  useEffect(() => {
    let newChips: ChipData[] = [];

    if (fieldValues && fieldValues.length > 0) {
      newChips = fieldValues.map((value) => {
        if (isAllowAllOption(value)) {
          return {
            id: value,
            name: ALLOW_ALL_OPTIONS[value as AllowAllOptionKey], // Use raw value instead of translation
          };
        }
        const existingItem = existingData?.find((item) => item.id === value);
        return {
          id: value,
          name: existingItem
            ? isUserEndpoint
              ? formatUserName(existingItem)
              : existingItem.name || ""
            : value,
        };
      });
    } else if (existingData && existingData.length > 0) {
      newChips = existingData.map((data) => ({
        id: data.id,
        name: isUserEndpoint ? formatUserName(data) : data.name || "",
      }));
    } else if (!isUserEndpoint) {
      if (
        id === "input_role_ids" &&
        otherFieldValues &&
        otherFieldValues.length === 0
      ) {
        newChips = [
          {
            id: "allow_all_roles",
            name: ALLOW_ALL_OPTIONS.allow_all_roles,
          },
        ];
      } else if (id === "input_customer_ids") {
        newChips = [
          {
            id: "allow_all_distributors",
            name: ALLOW_ALL_OPTIONS.allow_all_distributors,
          },
          {
            id: "allow_all_customers",
            name: ALLOW_ALL_OPTIONS.allow_all_customers,
          },
        ];
      }
    }

    setChips((currentChips) => {
      const newChipIds = newChips
        .map((chip) => chip.id)
        .sort()
        .join(",");
      const currentChipIds = currentChips
        .map((chip) => chip.id)
        .sort()
        .join(",");

      if (newChipIds !== currentChipIds) {
        return newChips;
      }
      return currentChips;
    });

    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
    }
  }, [fieldValuesKey, existingDataKey, isUserEndpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentChipIds = chips.map((chip) => chip.id);
    if (chipsChanged(previousChipIdsRef.current, currentChipIds)) {
      setValue(id, currentChipIds.length > 0 ? currentChipIds : undefined);
      previousChipIdsRef.current = currentChipIds;
    }
  }, [chips, setValue, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const formValues = getValues ? getValues() : {};
    if (isUserEndpoint) {
      if (otherFieldValues) {
        const hasAllRoles =
          otherFieldValues.includes("allow_all_roles") &&
          formValues?.input_role_ids;
        const previousHasAllRoles =
          previousOtherFieldValuesRef.current.includes("allow_all_roles");

        setIsDisabled(hasAllRoles);

        if (hasAllRoles && !previousHasAllRoles && chips.length > 0) {
          setChips([]);
          setValue(id, undefined);
          setValidationError("");
          clearErrors(id);
        }

        previousOtherFieldValuesRef.current = [...otherFieldValues];
      } else {
        setIsDisabled(false);
      }
    }
  }, [
    isUserEndpoint,
    otherFieldValues,
    chips,
    setValue,
    id,
    clearErrors,
    getValues,
  ]);

  const removeChip = useCallback(
    (name: string) => {
      setChips((currentChips) => {
        const remainingChips = currentChips.filter(
          (chip) => chip.name !== name
        );

        if (remainingChips.length === 0) {
          const isRoleField = id === "input_role_ids";
          const isUserField = id === "input_user_ids";

          let shouldPreventDeletion = true;

          if (isRoleField || isUserField) {
            const formValues = getValues ? getValues() : {};
            if (isRoleField) {
              shouldPreventDeletion =
                (formValues.input_user_ids || []).length === 0;
            } else if (isUserField) {
              shouldPreventDeletion =
                (formValues.input_role_ids || []).length === 0;
            }
          }

          if (shouldPreventDeletion) {
            const errorMessage = strings(t).atLeastOneItemRequired;
            setValidationError(errorMessage);
            setError(id, { message: errorMessage });
            return currentChips;
          }
        }

        setValidationError("");
        clearErrors(id);
        return remainingChips;
      });
    },
    [id, setError, clearErrors, t, getValues]
  );

  const clearChips = useCallback(() => {
    setChips([]);
    setValue(id, undefined);
    setValidationError("");
    clearErrors(id);
  }, [setValue, id, clearErrors]);

  const updateChips = useCallback(
    (newChips: ChipData[]) => {
      if (newChips.length > 0) {
        setChips(newChips);
        setValidationError("");
        clearErrors(id);
      }
    },
    [id, clearErrors]
  );

  return {
    chips,
    removeChip,
    clearChips,
    updateChips,
    validationError,
    isDisabled,
  };
};

const generateOptions = (
  response: any[],
  isUserEndpoint: boolean,
  isRoleEndpoint: boolean,
  isTenantEndpoint: boolean,
  selectedChips: ChipData[],
  t: any
): OptionType<string>[] => {
  const options: OptionType<string>[] = [];

  if (isRoleEndpoint) {
    options.push({
      value: "allow_all_roles",
      label: t("All Roles"),
    });
  }

  if (isTenantEndpoint) {
    options.push(
      { value: "allow_all_customers", label: t("All Customers") },
      { value: "allow_all_distributors", label: t("All Distributors") }
    );
  }

  options.push(
    ...response.map((item) => ({
      value: item.id,
      label: isUserEndpoint ? formatUserName(item) : item.name,
    }))
  );

  return options.filter(
    (item) => !selectedChips.some((chip) => chip.id === item.value)
  );
};

export const MultiSelectField = memo<MultiSelectFieldProps>(
  ({
    id,
    fieldValues,
    display_name,
    methodsOfUseForm,
    multiSelectRefs,
    baseUrl,
    existingData,
    userAndRolesValues,
  }) => {
    const { t } = useTranslation();
    const {
      register,
      setValue,
      setError,
      clearErrors,
      getValues,
      formState,
      formState: { errors },
    } = methodsOfUseForm;

    const endpointType = useMemo(
      () => ({
        isUser: baseUrl.includes("users"),
        isRole: baseUrl.includes("roles"),
        isTenant: baseUrl.endsWith("tenants-basic"),
      }),
      [baseUrl]
    );

    const {
      chips,
      removeChip,
      clearChips,
      updateChips,
      validationError,
      isDisabled,
    } = useChipManager(
      id,
      fieldValues,
      existingData,
      endpointType.isUser,
      setValue,
      setError,
      clearErrors,
      t,
      userAndRolesValues,
      getValues
    );

    const searchParams = useMemo(() => {
      const params = new URLSearchParams();
      params.append("order_by", "asc");
      if (endpointType.isUser) {
        params.append("is_active", "true");
      }
      return params;
    }, [endpointType.isUser]);

    const chipsRef = useRef<ChipData[]>(chips);

    useEffect(() => {
      chipsRef.current = chips;
    }, [chips]);

    const handleMultiSelectChange = useCallback(
      (data: CreatedOption | null) => {
        if (!isCreatedOption(data) || isDisabled) return;

        const { value, label } = data;
        const currentChips = chipsRef.current;

        if (isAllowAllOption(value)) {
          const allowAllIds = getAllowAllIds();
          const isAlreadySelected = currentChips.some(
            (chip) => chip.id === value
          );

          if (isAlreadySelected) {
            const chipToRemove = currentChips.find((chip) => chip.id === value);
            if (chipToRemove) {
              removeChip(chipToRemove.name);
            }
            return;
          }

          if (endpointType.isTenant) {
            const existingAllowAllOptions = currentChips.filter((chip) =>
              allowAllIds.includes(chip.id)
            );
            const newChip = {
              name: t(ALLOW_ALL_OPTIONS[value as AllowAllOptionKey]),
              id: value,
            };
            updateChips([...existingAllowAllOptions, newChip]);
          } else {
            const newChip = {
              name: t(ALLOW_ALL_OPTIONS[value as AllowAllOptionKey]),
              id: value,
            };
            updateChips([newChip]);
          }
        } else {
          const allowAllIds = getAllowAllIds();
          const filteredChips = currentChips.filter(
            (chip) => !allowAllIds.includes(chip.id)
          );
          const isAlreadySelected = filteredChips.some(
            (chip) => chip.id === value
          );

          if (!isAlreadySelected) {
            const newChip = { name: label, id: value };
            updateChips([...filteredChips, newChip]);
          }
        }
      },
      [endpointType.isTenant, t, removeChip, updateChips, isDisabled]
    );

    const getOptions = useCallback(
      (response: any[]): OptionType<string>[] => {
        return generateOptions(
          response,
          endpointType.isUser,
          endpointType.isRole,
          endpointType.isTenant,
          chips,
          t
        );
      },
      [endpointType, t, chips]
    );

    useEffect(() => {
      register(id, { required: false });
    }, [register, id]);

    useEffect(() => {
      if (multiSelectRefs.current) {
        multiSelectRefs.current[id] = { clearChips };
      }
    }, [id, multiSelectRefs]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div>
        <CompactInputWrapper>
          <SearchSelectInfiniteScroll
            errors={errors}
            formState={formState}
            placeholder={t("{{attribute}}", {
              attribute: t([display_name]),
            })}
            baseUrl={baseUrl}
            params={searchParams}
            getOptions={getOptions}
            onChange={handleMultiSelectChange}
            testid={id}
            showValue={false}
            disabled={isDisabled}
          />
        </CompactInputWrapper>

        <DeletableChips
          chips={chips}
          noneSelectedName={t([display_name])}
          handleClick={isDisabled ? () => {} : removeChip}
          showNoneSelectedMessage={false}
        />
        {validationError && (
          <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
            {validationError}
          </div>
        )}
      </div>
    );
  }
);
